import React from 'react';
import { MenuItem, ListItemIcon } from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';
import { RouteComponentProps } from 'react-router-dom';
import Logout from '@mui/icons-material/Logout'
import { resetEncryptedToken, resetLoggedInUser } from '../../redux/common-data-slice';
import stylesheet from '../patients/detail/detail-container.stylesheet'
import { useAppThunkDispatch } from '../../redux/store';
import { useAuth } from 'oidc-react';

interface IProps extends RouteComponentProps {
  history: any;
  location: any;
  match: any;
  handleLogout: any
}

const styles = makeStyles(() => ({
  menuItem: {
    padding: '0.2rem 0.4rem !important',
    fontSize: '0.7rem !important',
    '&.MuiMenuItem-root, & .MuiListItemIcon-root': {
      paddingLeft: '0.3rem !important',
    },
    '&:hover': {
      color: 'var(--color-primary) !important',
      '& .MuiListItemIcon-root': {
        color: 'var(--color-primary) !important',
      }
    },
  },
}));

const OidcLogoutComponent: React.FC<IProps> = ({ handleLogout }) => {
  const auth = useAuth() as any;  // Safe - only renders in OIDC mode
  const dispatch = useAppThunkDispatch();
  const classes = styles();
  const logout = async (): Promise<void> => {
    // Clear Redux state
    dispatch(resetLoggedInUser());
    dispatch(resetEncryptedToken());
    handleLogout();

    // Set logout flag FIRST so the login page shows "Sign In" instead of
    // auto-redirecting. sessionStorage persists across within-tab cross-origin
    // redirects (Azure AD end_session → back to origin/login).
    sessionStorage.setItem('userLoggedOut', 'true');

    // IMPORTANT: do NOT call localStorage.clear() / sessionStorage.clear() here.
    // signoutRedirect() reads the stored ID token to build the id_token_hint
    // param in the Azure AD end_session URL.  Without it, Azure AD may not close
    // the server session and SSO would auto-sign the user back in.
    //
    // oidc-client-ts automatically removes the stored user object before
    // redirecting, so the OIDC session is cleaned up on its own.
    // Our app-level token keys (access_token, encryptedToken, etc.) are cleared
    // in LoginOIDCCodeComponent when the next login completes.

    // Redirect through OIDC end_session endpoint to clear the Azure AD session.
    // Azure AD will redirect back to post_logout_redirect_uri (/login) when done.
    try {
      await auth.userManager.signoutRedirect({ post_logout_redirect_uri: `${window.location.origin}/login` });
    } catch {
      // Fallback: clear storage and go to /login directly
      localStorage.clear();
      sessionStorage.clear();
      sessionStorage.setItem('userLoggedOut', 'true');
      window.location.href = '/login';
    }
  };

  return (
    <MenuItem
      onClick={logout}
      className={classes.menuItem}
      title="Click to logout"
    >
      <ListItemIcon>
        <Logout fontSize="small" />
      </ListItemIcon>
      Logout
    </MenuItem>
  );
};

export default withStyles(stylesheet)(OidcLogoutComponent);